/**
 * MARVELAND 中央對角防盜水印（2026-08-23 用戶指示）
 *
 * 規格：
 * - 位置：畫面正中央，沿對角線方向（左下 → 右上）斜放
 * - 尺寸：文字長度 ≥ 畫面對角線長的 55%（跨越照片一半以上）
 * - 顏色：品牌金 #D4AF37
 * - 防移除設計（2026-08-27 起降級為「標記存在」優先，觀感優先於防移除）：
 *   1. 中央位置——裁切不掉（裁掉中央=廢圖）
 *   2. 極簡淡版：純半透明文字（主字 0.16 + 微重影 0.05），無 overlay 混合、無描邊
 * - 輸出 JPEG 0.92（破壞性編輯：原像素已永久改變，無法無損還原）
 * - SVG / GIF 跳過（圖標與動圖不適用）
 */

const WATERMARK_TEXT = 'MARVELAND';
// 2026-08-27 自適應對比：依照片中央亮度自動選色——
// 亮背景（白牆室內）用品牌深藍、暗背景用米白；確保「看得出有水印」且不喧賓奪主
const WM_DARK = '#0B1220'; // 亮背景用（品牌深藍）
const WM_LIGHT = '#EDEDED'; // 暗背景用（米白）

// 水印濃度（2026-08-28 起由 Admin 於後台調整，存 siteConfig.watermark.opacity）
// 主字＝opacity；重影＝opacity×0.3；0＝不加水印
export const DEFAULT_WATERMARK_OPACITY = 0.16;

export function watermarkApplicable(type: string): boolean {
  return type === 'image/jpeg' || type === 'image/png' || type === 'image/webp';
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality = 0.92): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

/**
 * 對圖片檔套用中央對角 MARVELAND 水印。
 * 回傳水印後的新 File（jpeg）；非適用類型回傳原檔。opacity ≤ 0 時回傳原檔（不加水印）。
 */
export async function applyDiagonalWatermark(
  file: File,
  opacity: number = DEFAULT_WATERMARK_OPACITY
): Promise<File> {
  if (!watermarkApplicable(file.type)) return file;
  if (opacity <= 0) return file;

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    const objectUrl = URL.createObjectURL(file);
    el.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(el);
    };
    el.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('圖片載入失敗'));
    };
    el.src = objectUrl;
  });

  const width = img.naturalWidth;
  const height = img.naturalHeight;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;

  ctx.drawImage(img, 0, 0, width, height);

  // 自適應對比：取畫面中央 40% 區域平均亮度決定水印顏色
  let color = WM_DARK;
  try {
    const sx = Math.round(width * 0.3);
    const sy = Math.round(height * 0.3);
    const sample = ctx.getImageData(sx, sy, Math.round(width * 0.4), Math.round(height * 0.4)).data;
    let lum = 0;
    let n = 0;
    for (let i = 0; i < sample.length; i += 40) {
      lum += 0.299 * sample[i] + 0.587 * sample[i + 1] + 0.114 * sample[i + 2];
      n++;
    }
    if (n > 0) color = lum / n > 140 ? WM_DARK : WM_LIGHT;
  } catch {
    // 取樣失敗沿用深色
  }

  // 對角線角度與目標長度（≥55% 對角線，跨越照片一半以上）
  const diagonal = Math.hypot(width, height);
  const angle = -Math.atan2(height, width); // 左下 → 右上
  const targetWidth = diagonal * 0.55;

  ctx.save();
  ctx.translate(width / 2, height / 2);
  ctx.rotate(angle);

  // 先以量測決定字級，讓文字寬度命中 targetWidth
  const weight = 900;
  const family = "'Arial Black', 'Noto Sans HK', sans-serif";
  let fontSize = 100;
  ctx.font = `${weight} ${fontSize}px ${family}`;
  const measured = ctx.measureText(WATERMARK_TEXT).width;
  fontSize = Math.round((fontSize * targetWidth) / measured);
  ctx.font = `${weight} ${fontSize}px ${family}`;

  // 字距（拉開增加修復難度）
  const letterSpacing = fontSize * 0.18;
  const withSpacing = (text: string): number => {
    let w = 0;
    for (const ch of text) w += ctx.measureText(ch).width + letterSpacing;
    return w;
  };
  const totalWidth = withSpacing(WATERMARK_TEXT);
  const startX = -totalWidth / 2;

  const drawPass = (alpha: number, mode: GlobalCompositeOperation, stroke: boolean) => {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.globalCompositeOperation = mode;
    let x = startX;
    for (const ch of WATERMARK_TEXT) {
      if (stroke) {
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(1, fontSize * 0.04);
        ctx.strokeText(ch, x, 0);
      }
      ctx.fillStyle = color;
      ctx.fillText(ch, x, 0);
      x += ctx.measureText(ch).width + letterSpacing;
    }
    ctx.restore();
  };

  // 極簡淡版基礎上的濃度可調（2026-08-28）：主字＝opacity、重影＝opacity×0.3
  // 純半透明、無混合特效、無描邊——觀感優先
  ctx.textBaseline = 'middle';
  drawPass(opacity, 'source-over', false);
  drawPass(opacity * 0.3, 'source-over', false);

  ctx.restore();

  const blob = await canvasToJpegBlob(canvas);
  if (!blob) return file;

  const originalName = file.name.replace(/\.[^.]+$/, '');
  return new File([blob], `${originalName}_watermarked.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/**
 * 後台設定用即時預覽：以指定濃度在畫布上繪製水印。
 * 有背景照片時以「cover」方式鋪滿畫布（模擬實際照片效果）；否則用淺色樣本漸層。
 * 與 applyDiagonalWatermark 同參數語義（主字 opacity、重影 ×0.3）。
 */
export function renderWatermarkPreview(
  canvas: HTMLCanvasElement,
  opacity: number,
  backgroundImage?: HTMLImageElement | null
): void {
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  if (backgroundImage && backgroundImage.naturalWidth > 0) {
    // cover：鋪滿並置中裁切
    const scale = Math.max(w / backgroundImage.naturalWidth, h / backgroundImage.naturalHeight);
    const dw = backgroundImage.naturalWidth * scale;
    const dh = backgroundImage.naturalHeight * scale;
    ctx.fillStyle = '#e5e5e5';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(backgroundImage, (w - dw) / 2, (h - dh) / 2, dw, dh);
  } else {
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, '#f5f4ef');
    grad.addColorStop(1, '#d9d8d0');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  }

  const diagonal = Math.hypot(w, h);
  const angle = -Math.atan2(h, w);
  const targetWidth = diagonal * 0.55;

  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.rotate(angle);

  const family = "'Arial Black', 'Noto Sans HK', sans-serif";
  let fontSize = 40;
  ctx.font = `900 ${fontSize}px ${family}`;
  const measured = ctx.measureText(WATERMARK_TEXT).width;
  fontSize = Math.round((fontSize * targetWidth) / measured);
  ctx.font = `900 ${fontSize}px ${family}`;

  const letterSpacing = fontSize * 0.18;
  let totalWidth = 0;
  for (const ch of WATERMARK_TEXT) totalWidth += ctx.measureText(ch).width + letterSpacing;
  const startX = -totalWidth / 2;
  ctx.textBaseline = 'middle';

  const draw = (alpha: number) => {
    ctx.save();
    ctx.globalAlpha = Math.max(0, alpha);
    let cx = startX;
    for (const ch of WATERMARK_TEXT) {
      ctx.fillStyle = WM_DARK;
      ctx.fillText(ch, cx, 0);
      cx += ctx.measureText(ch).width + letterSpacing;
    }
    ctx.restore();
  };

  draw(opacity);
  draw(opacity * 0.3);
  ctx.restore();
}
